import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, Droplets, Info } from 'lucide-react';
import axios from 'axios';
import WuduTutorial from './WuduTutorial';
import PrayerGuideModal from './PrayerGuideModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const PRAYER_DISPLAY = {
  Imsaku: { label: "Imsaku", icon: "🌙" },
  Lindja: { label: "Lindja", icon: "🌅", muted: true },
  Yleja: { label: "Yleja", icon: "☀️" },
  Ikindia: { label: "Ikindia", icon: "🌤️" },
  Akshami: { label: "Akshami", icon: "🌆" },
  Jacia: { label: "Jacia", icon: "🌙" },
};

const PRAYER_ORDER = ["Imsaku", "Lindja", "Yleja", "Ikindia", "Akshami", "Jacia"];
const FARD_PRAYER_ORDER = ["Imsaku", "Yleja", "Ikindia", "Akshami", "Jacia"];
const PRAYER_GUIDES = {
  Imsaku: "imsaku",
  Yleja: "dreka",
  Ikindia: "ikindia",
  Akshami: "akshami",
  Jacia: "jacia",
};

const PrayerTimesWidget = () => {
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [nextPrayer, setNextPrayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wuduOpen, setWuduOpen] = useState(false);
  const [selectedPrayer, setSelectedPrayer] = useState(null);

  useEffect(() => {
    fetchPrayerTimes();
    const interval = setInterval(fetchNextPrayer, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  const fetchPrayerTimes = async () => {
    try {
      const [timesRes, nextRes] = await Promise.all([
        axios.get(`${API}/prayer-times/today`),
        axios.get(`${API}/next-prayer`)
      ]);
      setPrayerTimes(timesRes.data);
      setNextPrayer(nextRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching prayer times:', error);
      setLoading(false);
    }
  };

  const fetchNextPrayer = async () => {
    try {
      const res = await axios.get(`${API}/next-prayer`);
      setNextPrayer(res.data);
    } catch (error) {
      console.error('Error fetching next prayer:', error);
    }
  };

  if (loading) {
    return (
      <motion.div 
        className="glass rounded-2xl p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="prayer-times-loading"
      >
        <p className="text-center">Duke ngarkuar kohët e namazit...</p>
      </motion.div>
    );
  }

  const prayers = PRAYER_ORDER.map((key) => ({
    key,
    time: prayerTimes?.[key],
    guideKey: PRAYER_GUIDES[key],
    ...PRAYER_DISPLAY[key],
  }));

  const nextPrayerName = nextPrayer?.name || nextPrayer?.prayer_name;
  const nextPrayerTime = nextPrayer?.time || nextPrayer?.prayer_time;
  const nextPrayerMinutes = nextPrayer?.minutes_left ?? nextPrayer?.time_remaining_minutes;
  const nextPrayerFormatted = nextPrayer?.time_remaining_formatted || formatMinutesLeft(nextPrayerMinutes);
  const nextPrayerIndex = FARD_PRAYER_ORDER.indexOf(nextPrayerName);
  const currentPrayerName = nextPrayerIndex > -1
    ? FARD_PRAYER_ORDER[(nextPrayerIndex - 1 + FARD_PRAYER_ORDER.length) % FARD_PRAYER_ORDER.length]
    : null;

  const handlePrayerClick = (guideKey) => {
    if (!guideKey) return;
    setSelectedPrayer(guideKey);
  };

  function formatMinutesLeft(minutes) {
    if (minutes === undefined || minutes === null) return "";
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  }

  return (
    <motion.div
      className="glass rounded-2xl p-6 md:p-8 max-w-md w-full"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="prayer-times-widget"
    >
      <div className="flex items-center gap-3 mb-6">
        <Clock className="w-6 h-6 text-accent" />
        <h3 className="text-2xl font-heading font-bold text-primary">Kohët e Namazit</h3>
      </div>

      {nextPrayer && (
        <div className="bg-accent/10 rounded-xl p-4 mb-6" data-testid="next-prayer-countdown">
          <p className="text-sm text-muted-foreground mb-1">Namazi i Ardhshëm</p>
          <p className="text-xl font-bold text-accent">{nextPrayerName}</p>
          <p className="text-2xl font-heading font-bold text-primary mt-1">{nextPrayerTime}</p>
          <p className="text-sm text-muted-foreground mt-2">
            Mbeten: <span className="font-semibold text-accent">{nextPrayerFormatted}</span>
          </p>
        </div>
      )}

      <div className="space-y-2">
        {prayers.map((prayer) => {
          const isCurrentPrayer = !prayer.muted && prayer.key === currentPrayerName;

          return (
            <button
              key={prayer.key}
              onClick={() => handlePrayerClick(prayer.guideKey)}
              disabled={prayer.muted}
              className={`w-full flex justify-between items-center py-3 px-3 border-b border-gray-200 last:border-0 rounded-lg transition-all group ${
                isCurrentPrayer
                  ? "bg-emerald-50 border-l-4 border-l-emerald-600"
                  : ""
              } ${
                prayer.muted
                  ? "cursor-default text-muted-foreground"
                  : "hover:bg-primary/5 cursor-pointer"
              }`}
              data-testid={`prayer-${prayer.key.toLowerCase()}`}
            >
              <div className="flex items-center gap-2">
                <span className="text-lg leading-none">{prayer.icon}</span>
                <span className={`font-medium ${prayer.muted ? "text-muted-foreground" : "text-foreground"}`}>
                  {prayer.label}
                </span>
                {isCurrentPrayer && (
                  <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                    Tani
                  </span>
                )}
                {prayer.guideKey && (
                  <Info className="w-4 h-4 text-accent opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
              </div>
              <span className={`text-lg font-heading font-semibold ${prayer.muted ? "text-muted-foreground" : "text-primary"}`}>
                {prayer.time}
              </span>
            </button>
          );
        })}
      </div>

      {/* Wudu Tutorial Button */}
      <button
        onClick={() => setWuduOpen(true)}
        className="w-full mt-4 flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-primary to-primary-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
        data-testid="wudu-tutorial-btn"
      >
        <Droplets className="w-5 h-5" />
        Si të Marrim Abdes
      </button>

      <p className="text-xs text-muted-foreground text-center mt-4">
        {new Date().toLocaleDateString('sq-AL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
      </p>
      <p className="text-xs text-accent text-center mt-1">
        Kliko mbi një namaz për udhëzues
      </p>

      {/* Wudu Tutorial Modal */}
      <WuduTutorial isOpen={wuduOpen} onClose={() => setWuduOpen(false)} />

      {/* Prayer Guide Modal */}
      <PrayerGuideModal 
        isOpen={!!selectedPrayer} 
        onClose={() => setSelectedPrayer(null)} 
        prayerKey={selectedPrayer}
      />
    </motion.div>
  );
};

export default PrayerTimesWidget;
